import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const useAuthStore = create(
  persist(
    (set, get) => ({
      // State
      user: null, // Teacher user data
      student: null, // Student user data (when logged in as student)
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      role: null, // 'teacher' or 'student'
      isLoading: false,
      error: null,

      // Actions
      setUser: (user) => set({ user, isAuthenticated: !!user }),

      setStudent: (student) => set({ student }),

      setTokens: (token, refreshToken) => set({ token, refreshToken }),

      setLoading: (isLoading) => set({ isLoading }),

      setError: (error) => set({ error }),

      // Teacher login
      loginAsTeacher: (userData, token, refreshToken) => set({
        user: userData,
        student: null,
        token,
        refreshToken,
        isAuthenticated: true,
        role: 'teacher',
        error: null,
      }),

      // Student login
      loginAsStudent: (studentData, token, refreshToken) => set({
        user: null,
        student: studentData,
        token,
        refreshToken,
        isAuthenticated: true,
        role: 'student',
        error: null,
      }),

      // Legacy login (defaults to teacher for backward compatibility)
      login: (userData, token, refreshToken) => set({
        user: userData,
        token,
        refreshToken,
        isAuthenticated: true,
        role: 'teacher',
        error: null,
      }),

      selectStudent: (student) => set({ student }),

      logout: () => set({
        user: null,
        student: null,
        token: null,
        refreshToken: null,
        isAuthenticated: false,
        role: null,
        error: null,
      }),

      updateUser: (userData) => set((state) => ({
        user: { ...state.user, ...userData },
      })),

      updateStudent: (studentData) => set((state) => ({
        student: state.student ? { ...state.student, ...studentData } : null,
      })),

      clearError: () => set({ error: null }),

      // Refresh student data from API response
      refreshStudentFromResponse: (responseData) => set((state) => {
        if (!state.student) return { student: null };

        // Calculate new badges count if badges were awarded
        const currentBadges = state.student.badges_earned || 0;
        const newBadgesAwarded = responseData.badges_earned?.length || 0;

        return {
          student: {
            ...state.student,
            total_points: responseData.new_total_points ?? responseData.total_points ?? state.student.total_points,
            streak_days: responseData.streak_days ?? state.student.streak_days,
            current_week: responseData.current_week ?? state.student.current_week,
            longest_streak: responseData.longest_streak ?? state.student.longest_streak,
            badges_earned: newBadgesAwarded > 0 ? currentBadges + newBadgesAwarded : currentBadges,
          },
        };
      }),

      // Getters
      getToken: () => get().token,
      getRefreshToken: () => get().refreshToken,
      getUser: () => get().user,
      getStudent: () => get().student,
      getRole: () => get().role,
      isTeacher: () => get().role === 'teacher',
      isStudentUser: () => get().role === 'student',
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        student: state.student,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        role: state.role,
      }),
    }
  )
);

export default useAuthStore;
